/**
 * Generic Calculator CTA Form Handler
 * Collects email and all calculator form data, then submits to API endpoint
 */

document.addEventListener("DOMContentLoaded", function () {
  const initCalculatorCTA = (ctaElement) => {
    const calculatorType = ctaElement.dataset.calculatorType
    const apiEndpoint = ctaElement.dataset.apiEndpoint
    const sectionId = ctaElement.dataset.sectionId
    const form = ctaElement.querySelector("[data-calculator-cta-form]")
    const nameInput = ctaElement.querySelector("[data-name-input]")
    const emailInput = ctaElement.querySelector("[data-email-input]")
    const companyInput = ctaElement.querySelector("[data-company-input]")
    const urlInput = ctaElement.querySelector("[data-url-input]")
    const submitButton = ctaElement.querySelector("[data-submit-button]")
    const buttonText = submitButton.querySelector(
      ".calculator-cta__button-text"
    )
    const buttonLoading = submitButton.querySelector(
      ".calculator-cta__button-loading"
    )
    const successMessage = ctaElement.querySelector("[data-success-message]")
    const errorMessage = ctaElement.querySelector("[data-error-message]")

    if (!form || !emailInput || !submitButton || !apiEndpoint) {
      console.warn("Calculator CTA: Missing required elements or API endpoint")
      return
    }

    // Find the calculator section (parent section)
    const calculatorSection = ctaElement.closest(
      calculatorType === "seo"
        ? ".seo-investment-calculator"
        : ".tco-calculator"
    )

    if (!calculatorSection) {
      console.warn(
        `Calculator CTA: Could not find calculator section for type: ${calculatorType}`
      )
      return
    }

    // Collect all calculator form data
    const collectCalculatorData = () => {
      const data = {
        calculatorType: calculatorType,
        timestamp: new Date().toISOString(),
      }

      if (calculatorType === "seo") {
        // Collect SEO calculator inputs
        const inputs = {
          baselineSessions: calculatorSection.querySelector(
            '[data-input="baselineSessions"]'
          ),
          baselineCvr: calculatorSection.querySelector(
            '[data-input="baselineCvr"]'
          ),
          aov: calculatorSection.querySelector('[data-input="aov"]'),
          contributionMargin: calculatorSection.querySelector(
            '[data-input="contributionMargin"]'
          ),
          seoRetainer: calculatorSection.querySelector(
            '[data-input="seoRetainer"]'
          ),
        }

        // Collect input values
        Object.keys(inputs).forEach((key) => {
          const input = inputs[key]
          if (input) {
            data[key] = input.value || input.checked || ""
          }
        })

        // Collect calculated outputs
        const outputs = {
          baselineMonthlyRevenue: calculatorSection.querySelector(
            '[data-output="baselineMonthlyRevenue"]'
          ),
          baselineAnnualRevenue: calculatorSection.querySelector(
            '[data-output="baselineAnnualRevenue"]'
          ),
          annualSeoCost: calculatorSection.querySelector(
            '[data-output="annualSeoCost"]'
          ),
          bestRoi: calculatorSection.querySelector('[data-output="bestRoi"]'),
          bestScenarioName: calculatorSection.querySelector(
            '[data-output="bestScenarioName"]'
          ),
          bestIncrementalRevenue: calculatorSection.querySelector(
            '[data-output="bestIncrementalRevenue"]'
          ),
          bestIncrementalProfit: calculatorSection.querySelector(
            '[data-output="bestIncrementalProfit"]'
          ),
          bestNewSessions: calculatorSection.querySelector(
            '[data-output="bestNewSessions"]'
          ),
        }

        Object.keys(outputs).forEach((key) => {
          const output = outputs[key]
          if (output) {
            data[key] = output.textContent?.trim() || ""
          }
        })
      } else if (calculatorType === "tco") {
        // Collect TCO calculator inputs
        const inputs = {
          monthlyGmv: calculatorSection.querySelector(
            '[data-input="monthlyGmv"]'
          ),
          avgOrderValue: calculatorSection.querySelector(
            '[data-input="avgOrderValue"]'
          ),
          shopifyPaymentsPercent: calculatorSection.querySelector(
            '[data-input="shopifyPaymentsPercent"]'
          ),
          shopifyPaymentsFixed: calculatorSection.querySelector(
            '[data-input="shopifyPaymentsFixed"]'
          ),
          monthlyAppCost: calculatorSection.querySelector(
            '[data-input="monthlyAppCost"]'
          ),
          monthlyDevCost: calculatorSection.querySelector(
            '[data-input="monthlyDevCost"]'
          ),
          shopifyPlusLicence: calculatorSection.querySelector(
            '[data-input="shopifyPlusLicence"]'
          ),
          useThirdPartyProvider: calculatorSection.querySelector(
            '[data-input="useThirdPartyProvider"]'
          ),
        }

        // Collect input values
        Object.keys(inputs).forEach((key) => {
          const input = inputs[key]
          if (input) {
            if (input.type === "checkbox") {
              data[key] = input.checked
            } else {
              data[key] = input.value || ""
            }
          }
        })

        // Collect calculated outputs
        const outputs = {
          ordersPerMonth: calculatorSection.querySelector(
            '[data-output="ordersPerMonth"]'
          ),
          monthlyTransactionFees: calculatorSection.querySelector(
            '[data-output="monthlyTransactionFees"]'
          ),
          monthlyAppDevCost: calculatorSection.querySelector(
            '[data-output="monthlyAppDevCost"]'
          ),
          tcoPercent: calculatorSection.querySelector(
            '[data-output="tcoPercent"]'
          ),
          monthlyTCO: calculatorSection.querySelector(
            '[data-output="monthlyTCO"]'
          ),
          annualTCO: calculatorSection.querySelector(
            '[data-output="annualTCO"]'
          ),
          tcoPercentHighlight: calculatorSection.querySelector(
            '[data-output="tcoPercentHighlight"]'
          ),
        }

        Object.keys(outputs).forEach((key) => {
          const output = outputs[key]
          if (output) {
            data[key] = output.textContent?.trim() || ""
          }
        })
      }

      return data
    }

    // Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault()

      // Hide previous messages
      if (successMessage) successMessage.classList.add("hidden")
      if (errorMessage) errorMessage.classList.add("hidden")

      // Collect and validate form data
      const formData = {
        name: nameInput ? nameInput.value.trim() : "",
        email: emailInput ? emailInput.value.trim() : "",
        company: companyInput ? companyInput.value.trim() : "",
        url: urlInput ? urlInput.value.trim() : "",
      }

      if (!formData.name || formData.name.length < 2) {
        if (errorMessage) {
          errorMessage.querySelector("p").textContent =
            "Please enter your full name"
          errorMessage.classList.remove("hidden")
        }
        return
      }

      if (!formData.email || !emailInput.checkValidity()) {
        if (errorMessage) {
          errorMessage.querySelector("p").textContent =
            "Please enter a valid email address"
          errorMessage.classList.remove("hidden")
        }
        return
      }

      if (!formData.company || formData.company.length < 2) {
        if (errorMessage) {
          errorMessage.querySelector("p").textContent =
            "Please enter your company name"
          errorMessage.classList.remove("hidden")
        }
        return
      }

      if (!formData.url || !urlInput.checkValidity()) {
        if (errorMessage) {
          errorMessage.querySelector("p").textContent =
            "Please enter a valid website URL"
          errorMessage.classList.remove("hidden")
        }
        return
      }

      // Disable form during submission
      submitButton.disabled = true
      buttonText?.classList.add("hidden")
      buttonLoading?.classList.remove("hidden")

      try {
        // Collect all calculator data
        const calculatorData = collectCalculatorData()

        // Prepare payload
        const payload = {
          ...formData,
          type: calculatorType,
          data: calculatorData,
        }
        console.log(payload)
        // Make API call
        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`)
        }

        // Show success message
        if (successMessage) {
          successMessage.classList.remove("hidden")
        }

        // Reset form
        form.reset()

        // Scroll to success message
        if (successMessage) {
          successMessage.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          })
        }
      } catch (error) {
        console.error("Calculator CTA submission error:", error)

        // Show error message
        if (errorMessage) {
          errorMessage.querySelector("p").textContent =
            error.message || "Something went wrong. Please try again."
          errorMessage.classList.remove("hidden")
        }
      } finally {
        // Re-enable form
        submitButton.disabled = false
        buttonText?.classList.remove("hidden")
        buttonLoading?.classList.add("hidden")
      }
    }

    // Attach submit handler
    form.addEventListener("submit", handleSubmit)
  }

  // Initialize all calculator CTA forms on the page
  const ctaElements = document.querySelectorAll(".calculator-cta")
  ctaElements.forEach(initCalculatorCTA)
})
